import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Users, Loader2, ChevronLeft, ChevronRight, Award, MapPin, Clock, Star } from 'lucide-react';
import { api } from '../../lib/api';
import { ExperienceLevelBadge } from '../../components/ExperienceLevelBadge';
import BulkPromoteCard from '../../components/admin/BulkPromoteCard';

const TalentPoolPage = () => {
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    seniority: '',
    country: '',
    availability: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchEmployees();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.role) params.append('role', filters.role);
      if (filters.seniority) params.append('seniority', filters.seniority);
      if (filters.country) params.append('country', filters.country);
      if (filters.availability) params.append('availability', filters.availability);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const response = await api.get(`/admin/talent-pool?${params.toString()}`);
      const data = await response.json();
      
      setEmployees(data.employees || []);
      setPagination({
        total: data.total || 0,
        pages: data.pages || 0
      });
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/talent-pool/stats/overview');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      role: '',
      seniority: '',
      country: '',
      availability: '',
      page: 1,
      limit: 20
    });
  };

  return (
    <div className="p-4 md:p-8" data-testid="talent-pool-page">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-semibold mb-2">Talent Pool</h1>
        <p className="text-muted-foreground">Talents validés prêts pour affectation</p>
      </div>

      {/* Bulk promote legacy `validated` */}
      <div className="mb-6">
        <BulkPromoteCard onComplete={() => { fetchEmployees(); fetchStats(); }} />
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-emerald-50">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total talents</p>
                <p className="text-2xl font-display font-semibold">{stats.total_employees}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-50">
                <Award className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Score</p>
                <p className="text-2xl font-display font-semibold">{stats.average_score}/100</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Top Roles</p>
              <div className="space-y-1">
                {stats.by_role?.slice(0, 2).map((item, idx) => (
                  <div key={idx} className="text-xs">
                    <span className="font-medium">{item._id}:</span> {item.count}
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Top Countries</p>
              <div className="space-y-1">
                {stats.by_country?.slice(0, 2).map((item, idx) => (
                  <div key={idx} className="text-xs">
                    <span className="font-medium">{item._id}:</span> {item.count}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="pl-10"
              data-testid="search-employees"
            />
          </div>

          <Select value={filters.role} onValueChange={(value) => setFilters({ ...filters, role: value === 'all' ? '' : value, page: 1 })}>
            <SelectTrigger data-testid="filter-role">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="Virtual Assistant">Virtual Assistant</SelectItem>
              <SelectItem value="Social Media Manager">Social Media Manager</SelectItem>
              <SelectItem value="Meta Ads Specialist">Meta Ads Specialist</SelectItem>
              <SelectItem value="Web Developer">Web Developer</SelectItem>
              <SelectItem value="SEO Specialist">SEO Specialist</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.seniority} onValueChange={(value) => setFilters({ ...filters, seniority: value === 'all' ? '' : value, page: 1 })}>
            <SelectTrigger data-testid="filter-seniority">
              <SelectValue placeholder="Seniority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="junior">Junior</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="senior">Senior</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.availability} onValueChange={(value) => setFilters({ ...filters, availability: value === 'all' ? '' : value, page: 1 })}>
            <SelectTrigger data-testid="filter-availability">
              <SelectValue placeholder="Availability" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="full-time">Full-time</SelectItem>
              <SelectItem value="part-time">Part-time</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Employee Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : employees.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {employees.map((employee) => (
              <Link key={employee.id} to={`/admin/talent-pool/${employee.id}`} data-testid={`employee-card-${employee.id}`}>
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-display font-semibold mb-1">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <p className="text-sm text-muted-foreground">{employee.main_profession}</p>
                    </div>
                    <ExperienceLevelBadge yearsOfExperience={employee.years_of_experience} size="sm" />
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{employee.country}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span className="capitalize">{employee.availability}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Award className="h-4 w-4" />
                      <span>{employee.years_of_experience || 0} an{(employee.years_of_experience || 0) > 1 ? 's' : ''} d'expérience</span>
                    </div>
                    {employee.rating?.average_score && (
                      <div className="flex items-center gap-1.5 text-sm">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{employee.rating.average_score.toFixed(1)}/5</span>
                        <span className="text-muted-foreground">
                          ({employee.rating.total_evaluations} avis)
                        </span>
                      </div>
                    )}
                    {employee.expected_salary_eur && (
                      <div className="text-sm font-medium text-[hsl(var(--primary))] mt-2">
                        €{employee.expected_salary_eur.toLocaleString()}/mo
                        {employee.expected_salary_local && employee.currency && (
                          <span className="text-xs text-muted-foreground ml-2">
                            ({employee.expected_salary_local.toLocaleString()} {employee.currency})
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {employee.ai_analysis?.skills && employee.ai_analysis.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {employee.ai_analysis.skills.slice(0, 4).map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                      {employee.ai_analysis.skills.length > 4 && (
                        <Badge variant="secondary" className="text-xs">
                          +{employee.ai_analysis.skills.length - 4}
                        </Badge>
                      )}
                    </div>
                  )}
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {employees.length} of {pagination.total} employees
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page === 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2">
                Page {filters.page} of {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page >= pagination.pages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Card className="p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No employees in talent pool yet</p>
          <p className="text-sm text-muted-foreground mt-2">
            Validated candidates will appear here
          </p>
        </Card>
      )}
    </div>
  );
};

export default TalentPoolPage;